import { supabase } from "@/integrations/supabase/client";

/**
 * Viral Victory Cards — achievement detector.
 * Reads ONLY real data from prediction_scores + nation_scores + profiles.
 * Returns empty when the user has no achievements (no mock fallbacks).
 */

export type AchievementType =
  | "first_correct"
  | "new_badge"
  | "top_predictor"
  | "nation_leader"
  | "top_10pct"
  | "top_1pct"
  | "streak"
  | "national_victory";

export interface Achievement {
  type: AchievementType;
  headline: string;
  subline?: string;
  emoji: string;
  payload: Record<string, unknown>;
  /** Stable id used to dedupe / key UI */
  id: string;
}

export interface UserContext {
  user_id: string;
  display_name: string | null;
  avatar_url: string | null;
  nation_code: string | null;
  nation_name: string | null;
  nation_flag: string | null;
  total_points: number;
  exact_predictions: number;
  predictions_total: number;
  streak_current: number;
  streak_best: number;
  global_rank: number | null;
  global_total_users: number;
  nation_rank: number | null;
  nation_total: number;
  badge_label: string | null;
}

const STREAK_TIERS = [3, 5, 10, 25, 50, 100];

function badgeFor(points: number): string | null {
  if (points >= 5000) return "Elite Predictor";
  if (points >= 1000) return "Master Predictor";
  if (points >= 250) return "Skilled Predictor";
  if (points >= 50) return "Rising Predictor";
  if (points >= 1) return "Rookie Predictor";
  return null;
}

export async function loadUserContext(): Promise<UserContext | null> {
  const { data: auth } = await supabase.auth.getUser();
  if (!auth.user) return null;
  const uid = auth.user.id;

  const [{ data: profile }, { data: score }] = await Promise.all([
    supabase
      .from("profiles")
      .select("display_name,avatar_url,selection_team")
      .eq("id", uid)
      .maybeSingle(),
    supabase
      .from("prediction_scores")
      .select("total_points,exact_predictions,predictions_total,streak_current,streak_best")
      .eq("user_id", uid)
      .maybeSingle(),
  ]);

  const nationCode = profile?.selection_team ?? null;
  let nation_name: string | null = null;
  let nation_flag: string | null = null;
  if (nationCode) {
    const { data: team } = await supabase
      .from("worldcup_teams")
      .select("name,flag")
      .eq("code", nationCode)
      .maybeSingle();
    nation_name = team?.name ?? null;
    nation_flag = team?.flag ?? null;
  }

  const total_points = Number(score?.total_points ?? 0);

  // Global rank — count of users with strictly higher points.
  const [{ count: higherCount }, { count: totalCount }] = await Promise.all([
    supabase
      .from("prediction_scores")
      .select("user_id", { count: "exact", head: true })
      .gt("total_points", total_points),
    supabase.from("prediction_scores").select("user_id", { count: "exact", head: true }).gt("total_points", 0),
  ]);
  const global_total_users = totalCount ?? 0;
  const global_rank = total_points > 0 ? (higherCount ?? 0) + 1 : null;

  // Nation rank
  let nation_rank: number | null = null;
  let nation_total = 0;
  if (nationCode) {
    const { data: ns } = await supabase
      .from("nation_scores")
      .select("nation_code,total_points")
      .order("total_points", { ascending: false });
    const list = (ns ?? []) as Array<{ nation_code: string; total_points: number }>;
    nation_total = list.length;
    const idx = list.findIndex((r) => r.nation_code === nationCode);
    nation_rank = idx >= 0 ? idx + 1 : null;
  }

  return {
    user_id: uid,
    display_name: profile?.display_name ?? null,
    avatar_url: profile?.avatar_url ?? null,
    nation_code: nationCode,
    nation_name,
    nation_flag,
    total_points,
    exact_predictions: Number(score?.exact_predictions ?? 0),
    predictions_total: Number(score?.predictions_total ?? 0),
    streak_current: Number(score?.streak_current ?? 0),
    streak_best: Number(score?.streak_best ?? 0),
    global_rank,
    global_total_users,
    nation_rank,
    nation_total,
    badge_label: badgeFor(total_points),
  };
}

export function computeAchievements(ctx: UserContext): Achievement[] {
  const out: Achievement[] = [];
  const flagPrefix = ctx.nation_flag ? `${ctx.nation_flag} ` : "";

  if (ctx.exact_predictions >= 1) {
    out.push({
      id: "first_correct",
      type: "first_correct",
      emoji: "🎯",
      headline: "Primer acierto",
      subline: `${ctx.exact_predictions} predicciones exactas`,
      payload: { exact_predictions: ctx.exact_predictions },
    });
  }

  if (ctx.badge_label) {
    out.push({
      id: `badge_${ctx.badge_label}`,
      type: "new_badge",
      emoji: "🏅",
      headline: `Nuevo Badge: ${ctx.badge_label}`,
      subline: `${ctx.total_points.toLocaleString()} XP acumulados`,
      payload: { badge: ctx.badge_label, points: ctx.total_points },
    });
  }

  if (ctx.global_rank && ctx.global_rank <= 10) {
    out.push({
      id: `top_predictor_${ctx.global_rank}`,
      type: "top_predictor",
      emoji: "🏆",
      headline: `Top Predictor #${ctx.global_rank}`,
      subline: `${ctx.total_points.toLocaleString()} XP`,
      payload: { rank: ctx.global_rank, total: ctx.global_total_users },
    });
  }

  if (ctx.global_rank && ctx.global_total_users > 0) {
    const pct = (ctx.global_rank / ctx.global_total_users) * 100;
    if (pct <= 1) {
      out.push({
        id: "top_1pct",
        type: "top_1pct",
        emoji: "💎",
        headline: "Entraste al Top 1%",
        subline: `#${ctx.global_rank} de ${ctx.global_total_users.toLocaleString()}`,
        payload: { rank: ctx.global_rank, total: ctx.global_total_users, percentile: 1 },
      });
    } else if (pct <= 10) {
      out.push({
        id: "top_10pct",
        type: "top_10pct",
        emoji: "🔥",
        headline: "Entraste al Top 10%",
        subline: `#${ctx.global_rank} de ${ctx.global_total_users.toLocaleString()}`,
        payload: { rank: ctx.global_rank, total: ctx.global_total_users, percentile: 10 },
      });
    }
  }

  const streakReached = [...STREAK_TIERS].reverse().find((t) => ctx.streak_best >= t);
  if (streakReached) {
    out.push({
      id: `streak_${streakReached}`,
      type: "streak",
      emoji: "🔥",
      headline: `Racha de ${streakReached} aciertos`,
      subline: ctx.streak_current >= streakReached ? "Racha activa" : `Mejor racha`,
      payload: { streak: streakReached, current: ctx.streak_current, best: ctx.streak_best },
    });
  }

  if (ctx.nation_rank === 1 && ctx.nation_name) {
    out.push({
      id: `nation_leader_${ctx.nation_code}`,
      type: "nation_leader",
      emoji: "👑",
      headline: `${flagPrefix}${ctx.nation_name} lidera Nation Wars`,
      subline: `${ctx.nation_total} naciones en disputa`,
      payload: { nation_code: ctx.nation_code, nation_name: ctx.nation_name, total: ctx.nation_total },
    });
  } else if (ctx.nation_rank && ctx.nation_rank <= 3 && ctx.nation_name) {
    out.push({
      id: `national_victory_${ctx.nation_code}_${ctx.nation_rank}`,
      type: "national_victory",
      emoji: ctx.nation_rank === 2 ? "🥈" : "🥉",
      headline: `${flagPrefix}${ctx.nation_name} en el podio mundial`,
      subline: `#${ctx.nation_rank} de ${ctx.nation_total} naciones`,
      payload: { nation_code: ctx.nation_code, nation_name: ctx.nation_name, rank: ctx.nation_rank },
    });
  }

  return out;
}
